import React, { useState, useRef, useEffect } from 'react';
import { Plus, MapPin, FileText, Package2, CheckSquare, X, Clock, Car, Minus, Calendar, Users, MessageSquare, Building2, DollarSign, Settings, BarChart3, TrendingUp, Archive, Wrench, User, Zap, ClipboardList, PieChart, UsersRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CreateBookingModal from './CreateBookingModal';
import CreateProposalModal from './CreateProposalModal';
import WithdrawStockModal from './WithdrawStockModal';
import HorizontalNavConfigModal from './HorizontalNavConfigModal';
import { loadHorizontalNavConfig, saveHorizontalNavConfig } from '../lib/horizontalNavConfig';

import { useBusiness } from '../contexts/BusinessContext';
import { useUser } from '../contexts/UserContext';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { supabase } from '../lib/supabase';
import CreateTaskModal from './tasks/CreateTaskModal';
import { getResources, Resource } from '../lib/resources';
import { hasActiveClockOutConstraint } from '../lib/tasks';
import CreateMobileBookingModal from './CreateMobileBookingModal';
import { usePermissionCheck } from '../hooks/usePermissionCheck';

interface NavItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  pageId?: string;
  permissionKey?: string;
}

interface FloatingActionButtonProps {
  permissionLevel?: 'manage' | 'view' | 'disabled';
  onSuccess?: () => Promise<void>;
}

export default function FloatingActionButton({ permissionLevel = 'manage', onSuccess }: FloatingActionButtonProps) {
  const { selectedBusiness } = useBusiness();
  const { user } = useUser();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();
  const navigate = useNavigate();
  const [showBookingOptions, setShowBookingOptions] = useState(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isConstraintModalOpen, setIsConstraintModalOpen] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [currentPayrollEntryId, setCurrentPayrollEntryId] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [compensationType, setCompensationType] = useState<'hourly' | 'flat_rate' | 'commission' | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const fabRef = useRef<HTMLDivElement>(null);
  const [showClockOutError, setShowClockOutError] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [showHorizontalNav, setShowHorizontalNav] = useState(false);
  
  // Horizontal navigation configuration state
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [currentConfigSlot, setCurrentConfigSlot] = useState<number>(0);
  const [horizontalNavItems, setHorizontalNavItems] = useState<(NavItem | null)[]>([null, null, null]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Message count state for badge display
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Update the checkClockInStatus function to also get compensation type
  useEffect(() => {
    const checkClockInStatus = async () => {
      if (!selectedBusiness?.id || !user?.id || !supabase) return;

      try {
        // First get the resource ID and compensation type for the authenticated user
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('id, compensation_type')
          .eq('auth_user_id', user.id)
          .eq('business_id', selectedBusiness.id)
          .single();

        if (resourceError) throw resourceError;
        if (!resourceData) return;

        setResourceId(resourceData.id);
        setCompensationType(resourceData.compensation_type);

        // Check if already clocked in
        const { data, error } = await supabase
          .from('payroll_page')
          .select('id, clock_in, break_start, break_end')
          .eq('resource_id', resourceData.id)
          .eq('business_id', selectedBusiness.id)
          .eq('entry_type', 'timeclock')
          .is('clock_out', null)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setClockedIn(true);
          setCurrentPayrollEntryId(data.id);
          setStartTime(new Date(data.clock_in));
          if (data.break_start && !data.break_end) {
            setOnBreak(true);
            setBreakStartTime(new Date(data.break_start));
          }
        }
      } catch (error) {
        console.error('Error checking clock in status:', error);
      }
    };

    checkClockInStatus();
  }, [selectedBusiness?.id, user?.id]);

  // Add timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (clockedIn && startTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        let diff = now.getTime() - startTime.getTime();
        
        // Subtract break time if on break
        if (onBreak && breakStartTime) {
          diff = diff - (now.getTime() - breakStartTime.getTime());
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [clockedIn, startTime, onBreak, breakStartTime]);

  // Fetch initial unread message count and set up realtime subscription for badge display
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Function to fetch current unread count
    const fetchUnreadCount = async () => {
      try {
        if (!supabase) {
          console.error('[FAB BADGE] Supabase client not initialized');
          return;
        }

        const { count, error } = await supabase
          .from('all_messages')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('read', 'unread')
          .eq('direction', 'inbound'); // Only count inbound unread messages

        if (error) {
          console.error('[FAB BADGE] Error fetching unread count:', error);
          return;
        }

        const newCount = count || 0;
        console.log('[FAB BADGE] 🔢 Updating badge count:', newCount);
        setUnreadMessagesCount(newCount);
      } catch (error) {
        console.error('[FAB BADGE] Error fetching unread count:', error);
      }
    };

    // Fetch initial count
    fetchUnreadCount();

    console.log('[FAB BADGE] 🔄 Setting up FAB navigation badge subscription for business:', selectedBusiness.id);

    // Set up realtime subscription with more specific event handling
    const subscription = supabase
      .channel(`unread_messages_fab_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[FAB BADGE] 📨 Received database event:', {
            eventType: payload.eventType,
            messageId: (payload.new as any)?.id || (payload.old as any)?.id,
            direction: (payload.new as any)?.direction || (payload.old as any)?.direction,
            read: (payload.new as any)?.read || (payload.old as any)?.read
          });
          
          // Add a small delay to ensure database consistency
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe((status) => {
        console.log('[FAB BADGE] 📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('[FAB BADGE] ✅ FAB navigation badge subscription active');
        }
      });

    // Set up a periodic refresh as backup (every 30 seconds)
    const periodicRefresh = setInterval(() => {
      console.log('[FAB BADGE] 🔄 Periodic refresh of badge count');
      fetchUnreadCount();
    }, 30000);

    // Listen for custom events from Messages page for immediate updates
    const handleUnreadMessagesChanged = () => {
      console.log('[FAB BADGE] 📢 Received local update event, refreshing badge');
      fetchUnreadCount();
    };

    window.addEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[FAB BADGE] 🧹 Cleaning up FAB navigation badge subscription');
      subscription.unsubscribe();
      clearInterval(periodicRefresh);
      window.removeEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);
    };
  }, [selectedBusiness?.id]);

  const handleClockIn = async () => {
    if (!selectedBusiness?.id || !user?.id || !supabase || !resourceId) return;

    try {
      const now = new Date();
      // Get resource's details for rate information
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', resourceId)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');

      // Create the payroll entry with all required fields
      const { data, error } = await supabase
        .from('payroll_page')
        .insert({
          business_id: selectedBusiness.id,
          resource_id: resourceId,
          clock_in: now.toISOString(),
          entry_type: 'timeclock',
          status: 'active',
          process_status: 'UNPAID',
          compensation_type: resourceData.compensation_type || 'hourly',
          rate_amount: resourceData.hourly_rate || 0,
          total_hours: 0,
          total_minutes: 0,
          total_earnings: 0
        })
        .select()
        .single();

      if (error) throw error;

      setClockedIn(true);
      setCurrentPayrollEntryId(data.id);
      setStartTime(now);
      setShowBookingOptions(false);
      setShowHorizontalNav(false);
    } catch (error) {
      console.error('Error clocking in:', error);
      alert('Failed to clock in. Please try again.');
    }
  };

  const handleClockOut = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId || !startTime) return;

    try {
      // Check for active clock out constraints
      const hasConstraint = await hasActiveClockOutConstraint(resourceId);
      if (hasConstraint) {
        setShowClockOutError(true);
        // Auto-hide the error after 3 seconds
        setTimeout(() => setShowClockOutError(false), 3000);
        return;
      }

      const now = new Date();

      // Get the current entry to calculate totals
      const { data: currentEntry, error: fetchError } = await supabase
        .from('payroll_page')
        .select('*, resource:resources(hourly_rate)')
        .eq('id', currentPayrollEntryId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentEntry) throw new Error('Entry not found');

      // Calculate total minutes worked
      let totalMs = now.getTime() - startTime.getTime();
      
      // Get all completed breaks for this entry
      const { data: breaks, error: breaksError } = await supabase
        .from('payroll_page')
        .select('break_start, break_end')
        .eq('id', currentPayrollEntryId)
        .not('break_start', 'is', null)
        .not('break_end', 'is', null);

      if (breaksError) throw breaksError;

      // Subtract all completed break times
      if (breaks) {
        breaks.forEach(breakPeriod => {
          const breakStart = new Date(breakPeriod.break_start);
          const breakEnd = new Date(breakPeriod.break_end);
          totalMs -= (breakEnd.getTime() - breakStart.getTime());
        });
      }

      // Calculate hours and earnings
      const totalMinutes = Math.round(totalMs / (1000 * 60));
      const hours = totalMinutes / 60;
      const hourlyRate = currentEntry.resource?.hourly_rate || 0;
      const totalEarnings = hours * hourlyRate;

      // Update the entry with final calculations
      const { error: updateError } = await supabase
        .from('payroll_page')
        .update({
          clock_out: now.toISOString(),
          total_minutes: totalMinutes,
          total_hours: hours,
          total_earnings: totalEarnings,
          status: 'completed'
        })
        .eq('id', currentPayrollEntryId)
        .eq('resource_id', resourceId);

      if (updateError) throw updateError;

      setClockedIn(false);
      setOnBreak(false);
      setCurrentPayrollEntryId(null);
      setStartTime(null);
      setBreakStartTime(null);
      setShowBookingOptions(false);
      setShowHorizontalNav(false);
    } catch (error) {
      console.error('Error clocking out:', error);
      alert('Failed to clock out. Please try again.');
    }
  };

  const handleBreak = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId) return;

    try {
      const now = new Date();
      if (!onBreak) {
        // Start break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_start: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(true);
        setBreakStartTime(now);
      } else {
        // End break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_end: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(false);
        setBreakStartTime(null);
      }
      setShowBookingOptions(false);
      setShowHorizontalNav(false);
    } catch (error) {
      console.error('Error handling break:', error);
      alert('Failed to update break status. Please try again.');
    }
  };

  // Handle click outside for dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setShowBookingOptions(false);
        setShowHorizontalNav(false);
        exitEditMode();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [longPressTimer]);

  const handleSuccess = async () => {
    if (onSuccess) {
      await onSuccess();
    }
    setShowBookingOptions(false);
    setShowHorizontalNav(false);
  };

  // Horizontal navigation handlers
  const handleConfigureSlot = (slotIndex: number) => {
    setCurrentConfigSlot(slotIndex);
    setIsConfigModalOpen(true);
    setShowBookingOptions(false);
    setShowHorizontalNav(false);
  };

  const handleNavItemSelect = async (item: NavItem) => {
    const newItems = [...horizontalNavItems];
    newItems[currentConfigSlot] = item;
    setHorizontalNavItems(newItems);

    // Save to database
    if (selectedBusiness?.id && user?.id) {
      try {
        await saveHorizontalNavConfig(
          selectedBusiness.id,
          user.id,
          newItems,
          resourceId || undefined
        );
      } catch (error) {
        console.error('Error saving horizontal nav config:', error);
      }
    }
  };

  const handleNavItemClick = (item: NavItem) => {
    // If in edit mode, don't navigate
    if (isEditMode) return;
    
    // Map navigation item IDs to their corresponding routes (in same order as left navigation)
    const navigationMap: Record<string, string> = {
      // Top navigation items
      'kpi-dashboard': '/kpi-dashboard',
      'calendar': '/calendarV2',
      'mobile-calendar': '/mobile-calendar',
      'bookings': '/bookings',
      'proposals': '/proposals',
      'customers': '/customers',
      
      // Manage section
      'messages': '/messages',
      'crm': '/crm',
      'payroll': '/payroll',
      'timeclock': '/timeclock',
      'supplies': '/stock',
      'services': '/services',
      'resources': '/resources',
      'automations': '/automations',
      'tasks': '/tasks',
      
      // Reports section
      'sales-reports': '/sales-reports',
      'team-reports': '/team-reports',
      'supplies-usage': '/supplies-usage',
      
      // Settings
      'settings': '/settings'
    };

    // Navigate to the corresponding page
    const route = navigationMap[item.id];
    if (route) {
      navigate(route);
    }
    
    // Close the menu
    setShowBookingOptions(false);
    setShowHorizontalNav(false);
  };

  // Long press handlers for edit mode
  const handleLongPressStart = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Clear any existing timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
    }
    
    // Start 3-second timer for edit mode
    const timer = setTimeout(() => {
      setIsEditMode(true);
      setLongPressTimer(null);
    }, 3000);
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    // Clear the timer if it hasn't fired yet
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Exit edit mode when clicking outside or using the main FAB
  const exitEditMode = () => {
    setIsEditMode(false);
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Remove navigation item and save to database
  const handleRemoveNavItem = async (slotIndex: number) => {
    const newItems = [...horizontalNavItems];
    newItems[slotIndex] = null;
    setHorizontalNavItems(newItems);

    // Save to database
    if (selectedBusiness?.id && user?.id) {
      try {
        await saveHorizontalNavConfig(
          selectedBusiness.id,
          user.id,
          newItems,
          resourceId || undefined
        );
      } catch (error) {
        console.error('Error saving horizontal nav config:', error);
      }
    }
  };

  useEffect(() => {
    if (isCreateTaskModalOpen && selectedBusiness?.id) {
      const fetchResources = async () => {
        try {
          const fetchedResources = await getResources(selectedBusiness.id);
          setResources(fetchedResources);
        } catch (error) {
          console.error('Error fetching resources:', error);
        }
      };
      fetchResources();
    }
  }, [isCreateTaskModalOpen, selectedBusiness?.id]);

  // Define available navigation items (shared with HorizontalNavConfigModal)
  // NOTE: Messages has been removed from available options as it's now a permanent standalone button
  const getAvailableNavItems = (): NavItem[] => [
    { id: 'kpi-dashboard', name: 'KPI Dashboard', icon: PieChart, color: 'bg-gray-500', pageId: 'kpi-dashboard', permissionKey: 'kpiDashboard' },
    { id: 'calendar', name: 'Location Calendar', icon: Calendar, color: 'bg-blue-500', pageId: 'calendarV2', permissionKey: 'calendar' },
    { id: 'mobile-calendar', name: 'Mobile Calendar', icon: Car, color: 'bg-green-500', pageId: 'mobile-calendar', permissionKey: 'calendar' },
    { id: 'bookings', name: 'Bookings', icon: MapPin, color: 'bg-purple-500', pageId: 'bookings', permissionKey: 'bookings' },
    { id: 'proposals', name: 'Proposals', icon: FileText, color: 'bg-orange-500', pageId: 'proposals', permissionKey: 'proposals' },
    { id: 'customers', name: 'Customers', icon: Users, color: 'bg-indigo-500', pageId: 'customers', permissionKey: 'customers' },
    { id: 'crm', name: 'CRM', icon: Building2, color: 'bg-blue-600', pageId: 'crm', permissionKey: 'crm' },
    { id: 'payroll', name: 'Payroll', icon: DollarSign, color: 'bg-teal-500', pageId: 'payroll', permissionKey: 'payroll' },
    { id: 'timeclock', name: 'Timeclock', icon: Clock, color: 'bg-amber-500', pageId: 'timeclock', permissionKey: 'timeclock' },
    { id: 'supplies', name: 'Supplies', icon: Package2, color: 'bg-yellow-500', pageId: 'supplies', permissionKey: 'supplies' },
    { id: 'services', name: 'Services', icon: Wrench, color: 'bg-pink-500', pageId: 'services', permissionKey: 'services' },
    { id: 'resources', name: 'Resources', icon: UsersRound, color: 'bg-cyan-500', pageId: 'resources', permissionKey: 'resources' },
    { id: 'automations', name: 'Automations', icon: Zap, color: 'bg-fuchsia-500', pageId: 'automations', permissionKey: 'automations' },
    { id: 'tasks', name: 'Tasks', icon: CheckSquare, color: 'bg-red-500', pageId: 'tasks', permissionKey: 'tasks' },
    { id: 'sales-reports', name: 'Sales Reports', icon: TrendingUp, color: 'bg-sky-500', pageId: 'sales-reports', permissionKey: 'salesReports' },
    { id: 'team-reports', name: 'Team Reports', icon: BarChart3, color: 'bg-stone-500', pageId: 'team-reports', permissionKey: 'teamReports' },
    { id: 'supplies-usage', name: 'Supplies Usage', icon: Archive, color: 'bg-zinc-500', pageId: 'supplies-usage', permissionKey: 'suppliesUsage' },
    { id: 'settings', name: 'Settings', icon: Settings, color: 'bg-slate-600', pageId: 'settings', permissionKey: 'settings' }
  ];

  // Convert NavItem ID to full NavItem object
  const getNavItemById = (id: string): NavItem | null => {
    const availableItems = getAvailableNavItems();
    return availableItems.find(item => item.id === id) || null;
  };

  // Load horizontal navigation configuration from database
  useEffect(() => {
    const loadConfig = async () => {
      if (!selectedBusiness?.id || !user?.id) return;

      try {
        const configSlots = await loadHorizontalNavConfig(
          selectedBusiness.id,
          user.id,
          resourceId || undefined
        );

        // Convert loaded config to full NavItem objects
        const loadedItems: (NavItem | null)[] = configSlots.map(slot => 
          slot?.id ? getNavItemById(slot.id) : null
        );

        setHorizontalNavItems(loadedItems);
      } catch (error) {
        console.error('Error loading horizontal nav config:', error);
      }
    };

    loadConfig();
  }, [selectedBusiness?.id, user?.id, resourceId]);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  return (
    <div>
      <div className="fixed bottom-6 right-6 z-50">
        <div ref={fabRef}>
          <div className="relative">
            <button 
              disabled={permissionLevel === 'view'}
              onClick={() => {
                if (isEditMode) {
                  exitEditMode();
                } else {
                  setShowBookingOptions(!showBookingOptions);
                  setShowHorizontalNav(!showHorizontalNav);
                }
              }}
              className={`fab-button p-3 text-white bg-gray-900 rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed ${
                showBookingOptions ? 'bg-gray-800' : ''
              }`}
            >
              <Plus className={`fab-button-icon h-5 w-5 ${showBookingOptions ? 'rotate-45' : ''}`} />
            </button>
            

          </div>
          
          {/* Permanent Messages Button - Mobile Only - Shown only when user has permissions, page is enabled, and FAB menu is closed */}
          {!isPageDisabled('messages') && canAccess.messages && (
            <div className={`
              md:hidden absolute bottom-16 left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out
              ${(showBookingOptions || showHorizontalNav || isEditMode)
                ? 'opacity-0 scale-95 pointer-events-none invisible'
                : 'opacity-100 scale-100 pointer-events-auto visible'}
            `}>
              <div className="relative">
                <button
                  onClick={() => navigate('/messages')}
                  className="p-4 text-white bg-lime-500 rounded-full hover:bg-lime-600 transition-all shadow-lg transform hover:scale-105"
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
                
                {/* Unread messages badge - always show when count > 0 */}
                {unreadMessagesCount > 0 && (
                  <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Horizontal Navigation - Mobile Only */}
          <div className={`
            md:hidden absolute bottom-0 right-full mr-8 flex items-center space-x-16 transform transition-all duration-300 ease-in-out
            ${(showHorizontalNav || isEditMode)
              ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto visible' 
              : 'opacity-0 translate-x-4 scale-95 pointer-events-none invisible'}
          `}>
            {horizontalNavItems.map((item, index) => {
              if (item) {
                // Render configured navigation item as a slightly larger circular button
                const IconComponent = item.icon;
                return (
                  <div key={index} className="relative">
                    <button
                      onClick={() => handleNavItemClick(item)}
                      onMouseDown={handleLongPressStart}
                      onMouseUp={handleLongPressEnd}
                      onMouseLeave={handleLongPressEnd}
                      onTouchStart={handleLongPressStart}
                      onTouchEnd={handleLongPressEnd}
                      className={`p-4 text-white ${item.color} rounded-full hover:opacity-90 transition-all shadow-lg transform hover:scale-105 ${
                        isEditMode ? 'animate-pulse' : ''
                      }`}
                    >
                      <IconComponent className="h-5 w-5" />
                    </button>
                    
                    {/* Message count badge - only show for messages item when count > 0 */}
                    {item.id === 'messages' && unreadMessagesCount > 0 && !isEditMode && (
                      <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg">
                        {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                      </div>
                    )}
                    
                    {/* Minus icon for edit mode */}
                    {isEditMode && (
                      <button
                        onClick={() => handleRemoveNavItem(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg animate-bounce"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              } else {
                // Render plus icon for empty slot
                return (
                  <button
                    key={index}
                    onClick={() => !isEditMode && handleConfigureSlot(index)}
                    className={`p-3 text-white bg-gray-400 rounded-full hover:bg-gray-500 transition-colors shadow-lg ${
                      isEditMode ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Plus className="h-4 w-4 opacity-60" />
                  </button>
                );
              }
            })}
          </div>
          
          {/* Quick Actions - Redesigned to match horizontal nav style */}
          <div className={`
            md:flex absolute bottom-full -right-3 mb-6 flex-col items-end space-y-3 transform transition-all duration-300 ease-in-out
            ${showBookingOptions 
              ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto visible' 
              : 'opacity-0 translate-y-2 scale-95 pointer-events-none invisible'}
          `}>
            
            {/* Timeclock Section - Special handling for hourly resources */}
            {compensationType === 'hourly' && (
              <div className="flex flex-col items-end space-y-2">
                {clockedIn ? (
                  <>
                    {/* Timer Display */}
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-right">
                      <div className="text-xs text-gray-500">Time Elapsed</div>
                      <div className="text-sm font-medium text-gray-900">{elapsedTime}</div>
                    </div>
                    
                    {/* Clock Out and Break buttons */}
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <button
                          onClick={handleBreak}
                          className={`p-3 text-white rounded-full hover:opacity-90 transition-all shadow-lg transform hover:scale-105 ${
                            onBreak ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          title={onBreak ? 'End Break' : 'Start Break'}
                        >
                          <Clock className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="relative">
                        <button
                          onClick={handleClockOut}
                          className="p-3 text-white bg-red-500 rounded-full hover:opacity-90 transition-all shadow-lg transform hover:scale-105"
                          title="Clock Out"
                        >
                          <X className="h-5 w-5" />
                        </button>
                        {showClockOutError && (
                          <div className="absolute bottom-full right-0 mb-2 w-48 bg-red-100 text-red-800 text-xs p-2 rounded shadow-lg">
                            Cannot clock out with active task
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={handleClockIn}
                    className="p-3 text-white bg-green-500 rounded-full hover:opacity-90 transition-all shadow-lg transform hover:scale-105"
                    title="Clock In"
                  >
                    <Clock className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            {/* Quick Action Buttons */}
            <div className="flex flex-col items-end space-y-3">
              {/* Location Booking */}
              {canAccess.calendar && !isPageDisabled('calendarV2') && (
                <button
                  onClick={() => {
                    setIsCreateBookingModalOpen(true);
                    setShowBookingOptions(false);
                    setShowHorizontalNav(false);
                  }}
                  className="flex items-center bg-white hover:bg-gray-50 rounded-full pr-3 pl-4 py-1 shadow-lg transform hover:scale-105 transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Location Booking
                  </span>
                  <div className="p-3 text-white bg-gray-600 group-hover:bg-gray-700 rounded-full transition-all">
                    <MapPin className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Mobile Booking */}
              {canAccess.calendar && !isPageDisabled('mobile-calendar') && (
                <button
                  onClick={() => {
                    setIsCreateMobileBookingModalOpen(true);
                    setShowBookingOptions(false);
                    setShowHorizontalNav(false);
                  }}
                  className="flex items-center bg-white hover:bg-gray-50 rounded-full pr-3 pl-4 py-1 shadow-lg transform hover:scale-105 transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Mobile Booking
                  </span>
                  <div className="p-3 text-white bg-gray-600 group-hover:bg-gray-700 rounded-full transition-all">
                    <Car className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Proposal */}
              {!isPageDisabled('proposals') && (
                <button
                  onClick={() => {
                    setIsCreateProposalModalOpen(true);
                    setShowBookingOptions(false);
                    setShowHorizontalNav(false);
                  }}
                  className="flex items-center bg-white hover:bg-gray-50 rounded-full pr-3 pl-4 py-1 shadow-lg transform hover:scale-105 transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Proposal
                  </span>
                  <div className="p-3 text-white bg-gray-600 group-hover:bg-gray-700 rounded-full transition-all">
                    <FileText className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Task */}
              {!isPageDisabled('tasks') && (
                <button
                  onClick={() => {
                    setIsCreateTaskModalOpen(true);
                    setShowBookingOptions(false);
                    setShowHorizontalNav(false);
                  }}
                  className="flex items-center bg-white hover:bg-gray-50 rounded-full pr-3 pl-4 py-1 shadow-lg transform hover:scale-105 transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Task
                  </span>
                  <div className="p-3 text-white bg-gray-600 group-hover:bg-gray-700 rounded-full transition-all">
                    <CheckSquare className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Withdraw Stock */}
              {!isPageDisabled('supplies') && (
                <button
                  onClick={() => {
                    setIsWithdrawStockModalOpen(true);
                    setShowBookingOptions(false);
                    setShowHorizontalNav(false);
                  }}
                  className="flex items-center bg-white hover:bg-gray-50 rounded-full pr-3 pl-4 py-1 shadow-lg transform hover:scale-105 transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Withdraw Stock
                  </span>
                  <div className="p-3 text-white bg-gray-600 group-hover:bg-gray-700 rounded-full transition-all">
                    <Package2 className="h-5 w-5" />
                  </div>
                </button>
              )}
            </div>
          </div>
        
        </div>
      </div>

      {isCreateTaskModalOpen && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          resources={resources}
          onSuccess={async () => {
            setIsCreateTaskModalOpen(false);
            if (onSuccess) {
              await onSuccess();
            }
          }}
        />
      )}

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => setIsCreateBookingModalOpen(false)}
        onSuccess={async () => {
          setIsCreateBookingModalOpen(false);
          await handleSuccess();
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async () => {
          setIsCreateProposalModalOpen(false);
          await handleSuccess();
        }}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          setIsWithdrawStockModalOpen(false);
          await handleSuccess();
        }}
      />



      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => setIsCreateMobileBookingModalOpen(false)}
        onSuccess={async () => {
          setIsCreateMobileBookingModalOpen(false);
          await handleSuccess();
        }}
      />

      <HorizontalNavConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        onSelect={handleNavItemSelect}
        slotIndex={currentConfigSlot}
      />
    </div>
  );
} 